/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css'
/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css'
import '@ionic/react/css/structure.css'
import '@ionic/react/css/typography.css'
/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css'
import '@ionic/react/css/float-elements.css'
import '@ionic/react/css/text-alignment.css'
import '@ionic/react/css/text-transformation.css'
import '@ionic/react/css/flex-utils.css'
import '@ionic/react/css/display.css'
/* Theme variables */
import './theme/variables.css'
import './theme/tailwind.css'
import './styles/globals.css'

import { IonApp, IonContent, IonMenu, IonRouterOutlet, IonSplitPane, setupIonicReact } from '@ionic/react'
import { createBrowserHistory } from 'history'
import { lazy, Suspense, useMemo } from 'react'
import { Provider, useSelector } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router-dom'

import AuthRoute from './components/AuthRoute'
import SuspenseRouter from './components/SuspenseRouter'
import TabViewHeader from './components/TabViewHeader'
import useAppResume from './hooks/useAppResume'
import useAppStart from './hooks/useAppStart'
import useCurrentPathname from './hooks/useCurrentPathname'
import lazyRetry from './lazyRetry'
import MainTabs, { allTabs } from './pages/MainTabs'
import store, { selectSession } from './store'

const SideMenu = lazy(() => import('./components/SideMenu'))

const Driver = lazy(() => lazyRetry(() => import('./pages/Driver')))
const ForgotPassword = lazy(() => lazyRetry(() => import('./pages/ForgotPassword')))
const Lock = lazy(() => lazyRetry(() => import('./pages/Lock')))
const Login = lazy(() => lazyRetry(() => import('./pages/Login')))
const Profile = lazy(() => lazyRetry(() => import('./pages/Profile')))
const GuestKeys = lazy(() => lazyRetry(() => import('./pages/GuestKeys')))
const Registration = lazy(() => lazyRetry(() => import('./pages/Registration')))
const Retail = lazy(() => lazyRetry(() => import('./pages/Retail')))
const QiotTest = lazy(() => lazyRetry(() => import('./pages/QiotTest/QiotTest')))
const Qiot = lazy(() => lazyRetry(() => import('./pages/Qiot/Qiot')))

setupIonicReact()

export const history = createBrowserHistory()

const App = () => {
  const { userType } = useSelector(selectSession)
  const currentPathname = useCurrentPathname(history)

  useAppStart(history)
  useAppResume()

  const shouldShowTabViewHeader = currentPathname === '/' || currentPathname.includes('/tabs')
  const shouldHideHeaderTextAndColor = currentPathname === '/' || currentPathname === '/tabs' || currentPathname === '/tabs/open-lock'
  const tabViewTitle = useMemo(() => allTabs.find((t) => t.href === currentPathname)?.label || '', [currentPathname])

  return (
    <IonApp>
      <SuspenseRouter history={history}>
        <Suspense>
          <IonSplitPane disabled={userType !== 'customer'} contentId="main">
            <IonMenu side="end" contentId="main" hidden={userType !== 'customer'}>
              <SideMenu />
            </IonMenu>
            <IonContent id="main">
              {shouldShowTabViewHeader && <TabViewHeader shouldHideHeaderTextAndColor={shouldHideHeaderTextAndColor} title={tabViewTitle} />}
              <IonRouterOutlet>
                <Switch>
                  <Route path="/login" render={() => <Login />} />
                  <Route path="/registration" render={() => <Registration defaultRoute="phone" />} exact />
                  <Route path="/retail" render={() => <Retail />} />
                  <Route path="/qiot/test" render={() => <QiotTest />} />
                  <Route path="/qiot" render={() => <Qiot />} />
                  <Route path="/forgot-password" render={() => <ForgotPassword />} />
                  <AuthRoute path="/locks/:lockId" render={() => <Lock />} />
                  <AuthRoute path="/driver" render={() => <Driver />} />
                  <AuthRoute path="/profile" render={() => <Profile />} />
                  <AuthRoute path="/guest-keys" render={() => <GuestKeys />} />
                  <AuthRoute path="/registration/choose-action" render={() => <Registration defaultRoute="chooseaction" />} exact />
                </Switch>
                <AuthRoute path="/tabs" render={() => <MainTabs />} />
                <AuthRoute
                  exact
                  path="/"
                  render={() => {
                    if (typeof userType !== 'string') return null

                    if (userType === 'customer') return <Redirect to="/tabs" />
                    if (userType === 'driver') return <Redirect to="/driver" />

                    return <Redirect to="/login" />
                  }}
                />
              </IonRouterOutlet>
            </IonContent>
          </IonSplitPane>
        </Suspense>
      </SuspenseRouter>
    </IonApp>
  )
}

const ConnectedApp = () => (
  <Provider store={store}>
    <App />
  </Provider>
)

export default ConnectedApp
